import React from "react";
import { Link, NavLink, useLocation, useParams, useNavigate } from "react-router-dom";
import { useAgents, useRenameAgent, useRemoveAgent } from "@/data/agentsStore";
import { authApi } from "@/lib/api";
import {
  Inbox as InboxIcon,
  Zap,
  Compass,
  Megaphone,
  HelpCircle,
  Settings as SettingsIcon,
  LogOut,
  Plus,
  MoreVertical,
  Pencil,
  BookOpen,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/* ===============  Tape title (newsprint highlight) =============== */
export function TapeTitle({ tape, rest, dark = false, className = "", testId }) {
  return (
    <h1
      data-testid={testId}
      className={`font-display font-black uppercase leading-[0.92] tracking-tight text-[44px] sm:text-[60px] lg:text-[72px] ${
        dark ? "text-white" : "text-[var(--ink)]"
      } ${className}`}
    >
      <span className={`tape ${dark ? "dark" : ""}`}>{tape}</span>{" "}
      <span className="italic font-medium">{rest}</span>
    </h1>
  );
}

/* ===============  Page header with blue rule =============== */
export function PageHeader({ eyebrow, tape, rest, sub, right, dark = false, testId }) {
  return (
    <header className="mb-10">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          {eyebrow && (
            <div
              className={`text-[11px] uppercase tracking-[0.22em] mb-3 ${
                dark ? "text-white/55" : "text-[var(--muted-light)]"
              }`}
            >
              {eyebrow}
            </div>
          )}
          <TapeTitle tape={tape} rest={rest} dark={dark} testId={testId} />
          <div className="mt-5 h-[3px] w-28 bg-[var(--blue)]" />
          {sub && (
            <p
              className={`mt-5 max-w-2xl text-[15px] leading-relaxed ${
                dark ? "text-white/65" : "text-[var(--muted-light)]"
              }`}
            >
              {sub}
            </p>
          )}
        </div>
        {right && <div className="flex items-center gap-3">{right}</div>}
      </div>
    </header>
  );
}

/* ===============  Top bar =============== */
export function TopBar() {
  return (
    <div
      className="h-12 bg-[var(--ink)] text-white flex items-center justify-between px-6 border-b border-[#1f1f1f] relative z-30"
      data-testid="top-bar"
    >
      <Link to="/" className="flex items-baseline gap-2 group" data-testid="top-bar-brand">
        <span className="font-display italic text-[20px] leading-none group-hover:text-[var(--blue)] transition-colors">
          LinkedIn cold outreach
        </span>
      </Link>
      <div className="flex items-center gap-6">
        <button
          onClick={async () => { await authApi.logout().catch(() => {}); window.location.href = "/admin/login"; }}
          className="text-[12px] uppercase tracking-[0.18em] text-white/70 hover:text-white flex items-center gap-2"
          data-testid="top-bar-logout"
        >
          <LogOut className="w-3.5 h-3.5" /> Logout
        </button>
      </div>
    </div>
  );
}

/* ===============  Sidebar =============== */
function SidebarAgentRow({ agent, onRename, onRemove }) {
  const params = useParams();
  const active = String(agent.id) === String(params.id);
  const maturityLetter = agent.maturity[0].toUpperCase();
  return (
    <div
      data-testid={`sidebar-agent-row-${agent.id}`}
      className={`group flex items-center border-l-2 transition-colors ${
        active
          ? "bg-white/[0.06] border-[var(--blue)]"
          : "border-transparent hover:bg-white/[0.04]"
      }`}
    >
      <NavLink
        to={`/admin/agents/${agent.id}`}
        data-testid={`sidebar-agent-${agent.id}`}
        className={`flex-1 flex items-center gap-2 px-3 py-2 text-[13px] min-w-0 ${
          active ? "text-white" : "text-white/70 hover:text-white"
        }`}
      >
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
            agent.active ? "bg-emerald-400" : "bg-amber-400"
          }`}
        />
        <span className="truncate flex-1">{agent.name}</span>
        <span
          className={`text-[10px] font-mono px-1 border shrink-0 ${
            active ? "border-white/60 text-white" : "border-white/20 text-white/55"
          }`}
        >
          {maturityLetter}
        </span>
      </NavLink>

      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid={`sidebar-agent-menu-${agent.id}`}
          aria-label={`Actions for ${agent.name}`}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100 text-white/55 hover:text-white px-2 py-2 transition-opacity"
        >
          <MoreVertical className="w-3.5 h-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40 rounded-none">
          <DropdownMenuItem
            data-testid={`sidebar-agent-rename-${agent.id}`}
            onClick={(e) => {
              e.preventDefault();
              onRename(agent);
            }}
            className="text-[12px] uppercase tracking-[0.16em] rounded-none"
          >
            <Pencil className="w-3.5 h-3.5 mr-2" /> Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`sidebar-agent-remove-${agent.id}`}
            onClick={(e) => {
              e.preventDefault();
              onRemove(agent);
            }}
            className="text-[12px] uppercase tracking-[0.16em] text-rose-600 focus:text-rose-700 rounded-none"
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function SidebarNavLink({ to, icon: Icon, label, badge, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 text-[12.5px] uppercase tracking-[0.14em] transition-colors ${
          isActive
            ? "text-white bg-white/[0.06] border-l-2 border-[var(--blue)]"
            : "text-white/65 hover:text-white border-l-2 border-transparent hover:bg-white/[0.04]"
        }`
      }
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="flex-1">{label}</span>
      {badge ? (
        <span className="text-[10px] font-mono bg-[var(--blue)] text-white px-1.5 leading-[16px]">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

export function Sidebar() {
  const agents = useAgents();
  const navigate = useNavigate();
  const params = useParams();
  const renameMut = useRenameAgent();
  const removeMut = useRemoveAgent();

  const [renameTarget, setRenameTarget] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [removeTarget, setRemoveTarget] = React.useState(null);

  function openRename(agent) {
    setRenameTarget(agent);
    setRenameValue(agent.name);
  }

  function confirmRename() {
    const name = renameValue.trim();
    if (!name || !renameTarget) return;
    renameMut.mutate({ id: renameTarget.id, name }, {
      onSuccess: () => toast(`Agent renamed to "${name}".`),
    });
    setRenameTarget(null);
  }

  function confirmRemove() {
    if (!removeTarget) return;
    const id = removeTarget.id;
    const name = removeTarget.name;
    removeMut.mutate(id, {
      onSuccess: () => toast(`Agent "${name}" removed.`),
    });
    setRemoveTarget(null);
    if (String(id) === String(params.id)) navigate("/admin/agents");
  }

  return (
    <aside
      className="w-[220px] shrink-0 bg-[var(--ink)] text-white h-[calc(100vh-48px)] flex flex-col border-r border-[#1f1f1f]"
      data-testid="sidebar"
    >
      {/* Zone A — new agent */}
      <div className="p-3 border-b border-[#1f1f1f]">
        <Link
          to="/admin/agents/new"
          data-testid="sidebar-new-agent"
          className="flex items-center justify-center gap-2 w-full h-9 bg-[var(--blue)] hover:bg-[var(--blue-hover)] text-white text-[11px] uppercase tracking-[0.2em] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> New agent
        </Link>
      </div>

      {/* Zone B — agent list */}
      <div className="flex-1 overflow-y-auto py-2">
        <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.24em] text-white/35">
          Agents
        </div>
        <div className="flex flex-col">
          {agents.length === 0 ? (
            <div className="px-3 py-4 text-[12px] text-white/40 italic">
              No agents. Add one →
            </div>
          ) : (
            agents.map((a) => (
              <SidebarAgentRow
                key={a.id}
                agent={a}
                onRename={openRename}
                onRemove={(agent) => setRemoveTarget(agent)}
              />
            ))
          )}
        </div>

        <div className="h-px bg-[#1f1f1f] my-4 mx-3" />

        <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.24em] text-white/35">
          Navigation
        </div>
        <div className="flex flex-col">
          <SidebarNavLink
            to="/admin/inbox"
            icon={InboxIcon}
            label="Inbox"
            badge={null}
            testId="sidebar-nav-inbox"
          />
          <SidebarNavLink to="/admin/blitz" icon={Zap} label="Blitz" testId="sidebar-nav-blitz" />
          <SidebarNavLink
            to="/admin/discover"
            icon={Compass}
            label="Discovery"
            testId="sidebar-nav-discovery"
          />
          <SidebarNavLink
            to="/admin/posts"
            icon={Megaphone}
            label="Posting Hub"
            testId="sidebar-nav-posts"
          />
          <SidebarNavLink
            to="/admin/knowledge"
            icon={BookOpen}
            label="Knowledge Base"
            testId="sidebar-nav-knowledge"
          />
        </div>
      </div>

      {/* Zone C — utilities */}
      <div className="border-t border-[#1f1f1f] py-2">
        <SidebarNavLink
          to="/admin/info"
          icon={HelpCircle}
          label="How it works"
          testId="sidebar-info"
        />
        <SidebarNavLink
          to="/admin/settings"
          icon={SettingsIcon}
          label="Settings"
          testId="sidebar-settings"
        />
      </div>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="rounded-none border border-[var(--rule-light)] sm:max-w-md" data-testid="rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[26px] uppercase tracking-tight">
              Rename <span className="italic">agent</span>
            </DialogTitle>
            <DialogDescription className="text-[var(--muted-light)] text-[13px]">
              Change the display name for{" "}
              <span className="font-mono text-[var(--ink)]">{renameTarget?.name}</span>. The
              underlying ID and Google Sheets binding stay the same.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <span className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)]">
              New name
            </span>
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmRename()}
              data-testid="rename-input"
              className="mt-2 w-full bg-transparent border-0 border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-2 text-[18px] font-display"
            />
          </div>
          <DialogFooter className="mt-6">
            <button
              onClick={() => setRenameTarget(null)}
              className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted-light)] hover:text-[var(--ink)] px-3 py-2"
              data-testid="rename-cancel"
            >
              Cancel
            </button>
            <button
              onClick={confirmRename}
              disabled={!renameValue.trim() || renameValue.trim() === renameTarget?.name}
              data-testid="rename-confirm"
              className="bg-[var(--blue)] hover:bg-[var(--blue-hover)] disabled:opacity-30 disabled:cursor-not-allowed text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em]"
            >
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation dialog */}
      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent className="rounded-none border border-[var(--rule-light)] sm:max-w-md" data-testid="remove-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[26px] uppercase tracking-tight">
              Remove <span className="italic">agent</span>
            </DialogTitle>
            <DialogDescription className="text-[var(--muted-light)] text-[13px] leading-relaxed">
              Permanently remove{" "}
              <span className="font-mono text-[var(--ink)]">{removeTarget?.name}</span> from this
              session. Its contacts, conversations and tick history will be hidden from the UI. The
              underlying LinkedIn account is not disconnected — only the dashboard binding is
              cleared.
            </DialogDescription>
          </DialogHeader>
          {removeTarget && (removeTarget.stats?.queuedMsgs > 0 || removeTarget.active) && (
            <div className="mt-3 border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
              Heads up — this agent is currently{" "}
              <strong>{removeTarget.active ? "active" : "paused"}</strong>
              {removeTarget.stats?.queuedMsgs > 0
                ? ` with ${removeTarget.stats.queuedMsgs} message${
                    removeTarget.stats.queuedMsgs === 1 ? "" : "s"
                  } in the send queue`
                : ""}
              .
            </div>
          )}
          <DialogFooter className="mt-6">
            <button
              onClick={() => setRemoveTarget(null)}
              className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted-light)] hover:text-[var(--ink)] px-3 py-2"
              data-testid="remove-cancel"
            >
              Cancel
            </button>
            <button
              onClick={confirmRemove}
              data-testid="remove-confirm"
              className="bg-rose-600 hover:bg-rose-700 text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em]"
            >
              Yes, remove agent
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}

/* ===============  App Shell =============== */
export function Shell({ children, paper = true }) {
  const location = useLocation();
  // Login is presented bare
  if (location.pathname.startsWith("/admin/login")) return <>{children}</>;

  return (
    <div className="h-screen flex flex-col bg-[var(--ink)]">
      <TopBar />
      <div className="flex-1 flex min-h-0">
        <Sidebar />
        <main
          className={`flex-1 overflow-y-auto ${
            paper ? "bg-[var(--paper)] on-paper" : "bg-[var(--ink)] text-white"
          }`}
          data-testid="main-content"
        >
          {children}
        </main>
      </div>
    </div>
  );
}

/* ===============  Small primitives =============== */
export function StatusDot({ active }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${
        active ? "bg-emerald-500" : "bg-amber-500"
      }`}
    />
  );
}

export function MaturityBadge({ maturity, dark = false }) {
  const map = { cold: "C", warm: "W", mature: "M" };
  const label = map[maturity] ?? "?";
  return (
    <span
      className={`inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 leading-[18px] border ${
        dark ? "border-white/40 text-white" : "border-[var(--ink)]/30 text-[var(--ink)]"
      }`}
    >
      {label}
    </span>
  );
}

const STATUS_COLOR = {
  NEW: "bg-gray-100 text-gray-700 border-gray-300",
  DISQUALIFIED: "bg-stone-100 text-stone-500 border-stone-300",
  RESEARCHED: "bg-slate-100 text-slate-700 border-slate-300",
  INVITED: "bg-amber-50 text-amber-700 border-amber-300",
  CONNECTED: "bg-indigo-50 text-indigo-700 border-indigo-300",
  SENT: "bg-blue-50 text-[var(--blue)] border-blue-300",
  FOLLOWED_UP: "bg-cyan-50 text-cyan-700 border-cyan-300",
  REPLIED: "bg-emerald-50 text-emerald-700 border-emerald-300",
  CLOSED_LOST: "bg-rose-50 text-rose-700 border-rose-300",
};

export function StatusBadge({ status, size = "sm" }) {
  const cls = STATUS_COLOR[status] ?? "bg-gray-100 text-gray-700 border-gray-300";
  return (
    <span
      className={`inline-flex items-center font-mono uppercase tracking-wider border px-1.5 ${
        size === "sm" ? "text-[10px] leading-[18px]" : "text-[11px] leading-[20px]"
      } ${cls}`}
    >
      {status}
    </span>
  );
}

const SCORE_COLOR = {
  HIGH: "text-rose-600 border-rose-300 bg-rose-50",
  MEDIUM_HIGH: "text-orange-600 border-orange-300 bg-orange-50",
  MEDIUM: "text-amber-600 border-amber-300 bg-amber-50",
  LOW_MEDIUM: "text-slate-600 border-slate-300 bg-slate-50",
  LOW: "text-gray-500 border-gray-300 bg-gray-50",
};
export function ScoreBadge({ score }) {
  if (!score) return null;
  const cls = SCORE_COLOR[score] ?? SCORE_COLOR.LOW;
  return (
    <span
      className={`text-[10px] font-mono uppercase border px-1.5 leading-[18px] ${cls}`}
    >
      {score.replace("_", " ")}
    </span>
  );
}

export function OfferBadge({ offerId, dark = false }) {
  if (!offerId) return <span className={dark ? "text-white/35" : "text-gray-400"}>—</span>;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 leading-[18px] border ${
        dark ? "border-white/40 text-white" : "border-[var(--blue)]/40 text-[var(--blue)] bg-blue-50"
      }`}
    >
      Offer {offerId}
    </span>
  );
}

export function Pill({ children, color = "default", className = "" }) {
  const map = {
    default: "bg-gray-100 text-gray-700 border-gray-300",
    blue: "bg-blue-50 text-[var(--blue)] border-blue-200",
    dark: "bg-[#1f1f1f] text-white/85 border-[#2f2f2f]",
  };
  return (
    <span
      className={`inline-flex items-center text-[11px] uppercase tracking-wider border px-2 py-0.5 ${map[color]} ${className}`}
    >
      {children}
    </span>
  );
}

export function EditorialButton({ children, variant = "primary", className = "", as = "button", ...rest }) {
  const Tag = as;
  const base =
    "inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 transition-all duration-150";
  const styles = {
    primary: "bg-[var(--blue)] text-white hover:bg-[var(--blue-hover)]",
    secondary:
      "border border-[var(--ink)]/25 text-[var(--ink)] hover:border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white",
    ghost:
      "text-[var(--ink)]/70 hover:text-[var(--ink)] underline-offset-4 hover:underline",
    dark:
      "border border-white/30 text-white hover:bg-white hover:text-[var(--ink)]",
    danger: "border border-rose-400 text-rose-600 hover:bg-rose-500 hover:text-white",
  };
  return (
    <Tag className={`${base} ${styles[variant]} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

export function SectionTitle({ children, eyebrow, className = "" }) {
  return (
    <div className={`mb-6 ${className}`}>
      {eyebrow && (
        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mb-2">
          {eyebrow}
        </div>
      )}
      <h2 className="font-display text-[28px] sm:text-[34px] leading-none uppercase tracking-tight">
        {children}
      </h2>
      <div className="mt-3 h-[2px] w-12 bg-[var(--blue)]" />
    </div>
  );
}
